import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	BoltIcon,
} from '@heroicons/react/24/outline';
import { getUtility, utilities } from '../../content/utilities';
import SunRaysHero from '../Services/SunRaysHero';

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

export default function UtilityDetail() {
	const { utilityId } = useParams();
	const utility = getUtility(utilityId);

	if (!utility)
		return (
			<Navigate
				to='/solar-with'
				replace
			/>
		);

	const others = utilities.filter((u) => u.id !== utility.id);
	const stepsTitle = utility.afterInstallTitle || 'What happens after installation?';

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<section className='relative isolate overflow-hidden border-b border-ink-100'>
				<SunRaysHero seed={6} />
				<div className='mx-auto max-w-7xl px-6 py-20 sm:py-28 lg:px-8'>
					<Link
						to='/solar-with'
						className='inline-flex items-center gap-2 text-sm font-semibold text-ink-500 hover:text-ink-900'
					>
						<ArrowLeftIcon className='h-4 w-4' />
						All utilities
					</Link>
					<div className='mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end'>
						<div className='lg:col-span-8'>
							<Eyebrow>Solar by utility</Eyebrow>
							<h1 className='font-display mt-5 text-5xl font-medium tracking-tight text-ink-900 sm:text-6xl leading-[1.05]'>
								{utility.label}
							</h1>
							<p className='mt-6 max-w-2xl text-lg leading-8 text-ink-600'>
								{utility.summary}
							</p>
						</div>
						<div className='lg:col-span-4'>
							<div className='rounded-2xl bg-white/70 p-6 ring-1 ring-ink-100 backdrop-blur-sm'>
								<BoltIcon className='h-9 w-9 text-primary-600' />
								<p className='mt-5 text-sm font-semibold text-ink-900'>
									{utility.fullName}
								</p>
								<p className='mt-1 text-sm leading-6 text-ink-600'>
									Serving {utility.region}.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Intro */}
			<section className='bg-white py-20 sm:py-28'>
				<div className='mx-auto max-w-3xl px-6 lg:px-8'>
					<div className='space-y-5 text-lg leading-8 text-ink-600'>
						{utility.intro.map((p) => (
							<p key={p}>{p}</p>
						))}
					</div>
				</div>
			</section>

			{/* Net metering */}
			<section className='bg-surface border-y border-ink-100 py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='grid grid-cols-1 gap-12 lg:grid-cols-12'>
						<div className='lg:col-span-5'>
							<Eyebrow>The basics</Eyebrow>
							<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
								How net metering works.
							</h2>
						</div>
						<div className='lg:col-span-7'>
							<div className='space-y-5 text-base leading-7 text-ink-600'>
								{utility.netMetering.map((p) => (
									<p key={p}>{p}</p>
								))}
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* After install / process */}
			<section className='bg-white py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='grid grid-cols-1 gap-12 lg:grid-cols-12'>
						<div className='lg:col-span-5'>
							<Eyebrow>The process</Eyebrow>
							<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
								{stepsTitle}
							</h2>
							<p className='mt-6 text-base leading-7 text-ink-500'>
								{utility.afterInstallNote}
							</p>
						</div>
						<div className='lg:col-span-7'>
							<ol className='space-y-4'>
								{utility.afterInstall.map((step, i) => (
									<li
										key={step}
										className='flex items-start gap-4 sg-card p-5'
									>
										<span className='font-display flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700'>
											{i + 1}
										</span>
										<span className='pt-1 text-sm leading-6 text-ink-700'>
											{step}
										</span>
									</li>
								))}
							</ol>
						</div>
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className='bg-surface border-t border-ink-100 py-20'>
				<div className='mx-auto max-w-3xl px-6 text-center lg:px-8'>
					<h2 className='font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
						{utility.name} customer? Let&apos;s look at your bill.
					</h2>
					<p className='mt-4 text-base leading-7 text-ink-500'>
						Send your latest {utility.name} bill and I&apos;ll model your savings
						and walk you through every step — start to PTO.
					</p>
					<div className='mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-4'>
						<Link
							to='/contact'
							className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-primary-500/25 hover:bg-primary-600 transition-all'
						>
							Schedule a free solar assessment
							<ArrowRightIcon className='h-5 w-5' />
						</Link>
						<Link
							to='/ask-gabe'
							className='text-sm font-semibold text-ink-700 hover:text-primary-600'
						>
							or ask Gabe a question
						</Link>
					</div>
				</div>
			</section>

			{/* Other utilities */}
			<section className='bg-white py-20'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<h2 className='font-display text-3xl font-medium tracking-tight text-ink-900'>
						Other utilities
					</h2>
					<div className='mt-8 grid grid-cols-1 gap-4 md:grid-cols-3'>
						{others.map((u) => (
							<Link
								key={u.id}
								to={`/solar-with/${u.id}`}
								className='group sg-card sg-card-interactive p-6'
							>
								<h3 className='font-display text-xl font-medium text-ink-900'>
									{u.label}
								</h3>
								<p className='mt-3 flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all'>
									View <ArrowRightIcon className='h-4 w-4' />
								</p>
							</Link>
						))}
					</div>
				</div>
			</section>
		</div>
	);
}
